
import api from './core';
import { Customer, CustomerStats } from './types';
import { normalizePaginatedResponse, type PaginatedResponse } from './pagination';

const MAX_PAGE_SIZE = 200;

export interface GetCustomersParams {
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

export const getCustomers = async (
  params?: GetCustomersParams,
  signal?: AbortSignal
): Promise<PaginatedResponse<Customer>> => {
  const query = new URLSearchParams();
  if (params?.search?.trim()) query.set('search', params.search.trim());
  if (params?.ordering) query.set('ordering', params.ordering);
  if (params?.limit != null) query.set('limit', String(Math.min(Number(params.limit), MAX_PAGE_SIZE)));
  if (params?.offset != null) query.set('offset', String(params.offset));
  const qs = query.toString();
  const url = `/crm/customers/${qs ? `?${qs}` : ''}`;
  const response = await api.get(url, { signal });
  return normalizePaginatedResponse(response.data);
};

const _fetchAllCustomers = async (signal?: AbortSignal): Promise<Customer[]> => {
  const result = await getCustomers({ limit: MAX_PAGE_SIZE }, signal);
  if (result.total > result.results.length) {
    console.error(`[getAllCustomers] Only fetched ${result.results.length} of ${result.total} customers. Customer list is incomplete.`);
  }
  return result.results;
};

// Single-flight: the auth flow fetches the full customer list from several
// init paths on load (a heavy query). If one is already in flight, share it.
// Cancelable (signal'd) callers bypass the shared promise to keep abort semantics.
let inflightAllCustomers: Promise<Customer[]> | null = null;

/**
 * Get all customers as a flat array (unpaginated).
 * Used by auth/customer-selection flows that need the full list.
 */
export const getAllCustomers = async (signal?: AbortSignal): Promise<Customer[]> => {
  if (signal) return _fetchAllCustomers(signal);
  if (inflightAllCustomers) return inflightAllCustomers;
  inflightAllCustomers = _fetchAllCustomers().finally(() => {
    inflightAllCustomers = null;
  });
  return inflightAllCustomers;
};

export const getCustomer = async (
  id: string,
  signal?: AbortSignal
): Promise<Customer> => {
  const response = await api.get(`/crm/customers/${id}/`, { signal });
  return response.data;
};

export const getCustomerStats = async (
  id: string,
  signal?: AbortSignal
): Promise<CustomerStats> => {
  const response = await api.get(`/crm/customers/${id}/stats/`, { signal });
  return response.data;
};

export const setActiveCustomer = async (customerId: string): Promise<void> => {
  await api.put(`/crm/customers/active/${customerId}/`);
};
